// traceBeliefStore.js — the file system as the agent believes it, replayable
// to any point in the trace.
//
// An agent acts against an endpoint whose state it partially knows, and many
// of its actions are only invertible against that knowledge: `sed -i` states
// a TRANSFORM, not a diff, and a Python script that rewrites a file states
// the new content but not what it displaced. The per-file replay already
// exists (reconstructFile renders a lane's as-of state); this store is the
// same replay turned into a QUERYABLE substrate — "the full content of PATH
// as believed just before THIS call" — so derivation-shaped extractors
// (traceDerivedFileOps) can turn transforms into real hunks.
//
// Everything here is deterministic and observation-grounded: beliefs come
// only from bytes the trace holds (reads observed, writes and edits stated),
// and a query answers null unless the belief is COMPLETE — full length known,
// every line known, no blind edits. Deriving hunks from a half-known file
// would state changes the agent never made, which is worse than staying
// silent; a caller that gets null falls back to the shape-only reading.

import { extractFileOps, reconstructFile } from "./traceFileTree.js";

// Block order as a single comparable ordinal. Blocks per turn are bounded
// well under this stride in practice; a pathological turn only weakens
// ordering within itself.
const STRIDE = 100000;
// Distinct replay points per path (see contentBefore) — the quadratic guard.
const REPLAYS_PER_PATH_CAP = 32;

/**
 * Build the store for one trace. `fileOps` may be passed when the caller
 * already extracted them (buildSemanticFeed does) so the replay and the feed
 * read the identical op stream.
 *
 * Returns { contentBefore(path, key) } — the believed full text of `path`
 * strictly before the tool_use block `key` belongs to (suffixes ignored), or
 * null when the belief is absent or incomplete.
 */
export function buildBeliefStore(turns, fileOps = null) {
  const ordOf = new Map();
  (Array.isArray(turns) ? turns : []).forEach((turn, ti) => {
    (turn?.blocks || []).forEach((b, bi) => {
      if (b?.type === "tool_use" && b.id) ordOf.set(b.id, ti * STRIDE + bi);
    });
  });

  const byPath = new Map();
  for (const op of fileOps ?? extractFileOps(turns)) {
    if (!op?.path) continue;
    const at = ordOf.get(String(op.key).split("#")[0]);
    if (at == null) continue; // unplaceable op — cannot participate in "before"
    let seq = byPath.get(op.path);
    if (!seq) {
      seq = [];
      byPath.set(op.path, seq);
    }
    seq.push({ at, op });
  }
  for (const seq of byPath.values()) seq.sort((a, b) => a.at - b.at);

  const cache = new Map();
  const replays = new Map();

  const contentBefore = (path, key) => {
    const base = String(key || "").split("#")[0];
    const at = ordOf.get(base);
    const seq = byPath.get(path);
    if (at == null || !seq) return null;
    const cacheKey = `${path} ${at}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    // Each DISTINCT query point replays the path's prefix from scratch, so K
    // sequential transforms of one file cost O(K^2) on the render path. Real
    // traces sit nowhere near the bound; a pathological one degrades to
    // shape-only reading instead of a stall.
    const replayed = (replays.get(path) || 0) + 1;
    if (replayed > REPLAYS_PER_PATH_CAP) return null;
    replays.set(path, replayed);
    const rec = reconstructFile(seq.filter((e) => e.at < at).map((e) => e.op));
    // Complete belief only: full length pinned, no gaps, no edits that never
    // found their anchor.
    let out = null;
    if (rec.lengthKnown && rec.blindEdits === 0 && rec.known === rec.total) {
      const lines = [];
      let complete = true;
      for (const r of rec.rows) {
        if (r.gap) { complete = false; break; }
        if (r.del) continue;
        lines.push(r.text);
      }
      if (complete && lines.length === rec.total) out = lines.join("\n");
    }
    cache.set(cacheKey, out);
    return out;
  };

  return { contentBefore };
}
